import React, { useMemo, useState } from "react";
import { Boxes, Plus, Pencil, PackagePlus, Search, Tag, Trash2, X, DollarSign, TrendingUp, Wallet, ListChecks } from "lucide-react";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";

const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm";
const brl = (v) => (Number(v) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const emptyProd = { nome: "", categoria: "Medicamento", unidade: "un", precoCompra: 0, precoVenda: 0, estoqueMinimo: 0, estoqueInicial: 0 };

const Produtos = () => {
  const { produtos, categorias, salvarProduto, entradaEstoqueEmpresa, excluirProduto, salvarCategoria, excluirCategoria, salvarPrecosMassa, isAdmin } = useApp();
  const { toast } = useToast();
  const [q, setQ] = useState("");
  const [excluir, setExcluir] = useState(null);
  const [excluindo, setExcluindo] = useState(false);
  const [filtroCat, setFiltroCat] = useState("todas");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProd);
  const [catOpen, setCatOpen] = useState(false);
  const [novaCat, setNovaCat] = useState("");
  const [editCat, setEditCat] = useState(null);
  const [entradaId, setEntradaId] = useState(null);
  const [qtd, setQtd] = useState("");
  const [massaOpen, setMassaOpen] = useState(false);
  const [massaBusca, setMassaBusca] = useState("");
  const [massaCat, setMassaCat] = useState("todas");
  const [precos, setPrecos] = useState({});
  const [salvandoMassa, setSalvandoMassa] = useState(false);

  const cats = categorias?.map((c) => c.nome) || [];
  const s = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Resumo financeiro do estoque (margem de lucro)
  const resumo = useMemo(() => {
    let investido = 0;
    let potencial = 0;
    produtos.forEach((p) => {
      const est = p.estoqueEmpresa || 0;
      investido += (Number(p.precoCompra) || 0) * est;
      potencial += (Number(p.precoVenda) || 0) * est;
    });
    return { investido, potencial, margem: potencial - investido };
  }, [produtos]);

  const lista = useMemo(() => {
    return [...produtos]
      .filter((p) => filtroCat === "todas" || p.categoria === filtroCat)
      .filter((p) => p.nome.toLowerCase().includes(q.toLowerCase()))
      .sort((a, b) => a.categoria.localeCompare(b.categoria) || a.nome.localeCompare(b.nome));
  }, [produtos, q, filtroCat]);

  const listaMassa = useMemo(() => {
    return [...produtos]
      .filter((p) => massaCat === "todas" || p.categoria === massaCat)
      .filter((p) => p.nome.toLowerCase().includes(massaBusca.toLowerCase()))
      .sort((a, b) => a.categoria.localeCompare(b.categoria) || a.nome.localeCompare(b.nome));
  }, [produtos, massaBusca, massaCat]);

  const abrirMassa = () => {
    const init = {};
    produtos.forEach((p) => { init[p.id] = { precoCompra: p.precoCompra || 0, precoVenda: p.precoVenda || 0 }; });
    setPrecos(init);
    setMassaBusca(""); setMassaCat("todas");
    setMassaOpen(true);
  };
  const setPreco = (id, campo, val) => setPrecos((p) => ({ ...p, [id]: { ...p[id], [campo]: parseFloat(val) || 0 } }));
  const salvarMassa = async () => {
    const itens = produtos
      .filter((p) => precos[p.id] && (precos[p.id].precoCompra !== (p.precoCompra || 0) || precos[p.id].precoVenda !== (p.precoVenda || 0)))
      .map((p) => ({ id: p.id, precoCompra: precos[p.id].precoCompra, precoVenda: precos[p.id].precoVenda }));
    if (itens.length === 0) { toast({ title: "Nenhuma alteração de preço" }); return; }
    setSalvandoMassa(true);
    try {
      await salvarPrecosMassa(itens);
      toast({ title: `${itens.length} preço(s) atualizado(s)` });
      setMassaOpen(false);
    } finally { setSalvandoMassa(false); }
  };

  const abrirNovo = () => { setEditing(null); setForm({ ...emptyProd, categoria: cats[0] || "Outros" }); setOpen(true); };
  const abrirEdit = (p) => { setEditing(p); setForm({ ...emptyProd, ...p }); setOpen(true); };
  const salvar = async () => {
    if (!form.nome.trim()) { toast({ title: "Informe o nome do produto" }); return; }
    await salvarProduto({ ...form, id: editing?.id });
    setOpen(false);
    toast({ title: editing ? "Produto atualizado" : "Produto cadastrado" });
  };
  const registrarEntrada = async () => {
    if (!qtd || Number(qtd) <= 0) return;
    await entradaEstoqueEmpresa(entradaId, Number(qtd));
    setEntradaId(null); setQtd("");
    toast({ title: "Entrada de estoque registrada" });
  };
  const confirmarExcluir = async () => {
    if (!excluir) return;
    setExcluindo(true);
    try {
      await excluirProduto(excluir.id);
      toast({ title: "Produto excluído" });
      setExcluir(null);
    } catch (e) {
      toast({ title: "Não foi possível excluir", description: e?.response?.data?.detail || "Erro ao excluir o produto." });
    } finally { setExcluindo(false); }
  };
  const addCategoria = async () => {
    if (!novaCat.trim()) return;
    try { await salvarCategoria({ nome: novaCat.trim() }); setNovaCat(""); toast({ title: "Categoria adicionada" }); }
    catch (e) { toast({ title: "Erro", description: e?.response?.data?.detail || "Não foi possível." }); }
  };
  const salvarEdicaoCat = async () => {
    if (!editCat?.nome?.trim()) return;
    await salvarCategoria(editCat); setEditCat(null); toast({ title: "Categoria atualizada" });
  };

  return (
    <div className="space-y-6" data-testid="produtos-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Boxes className="w-6 h-6 text-blue-600" /> Produtos</h1>
          <p className="text-sm text-slate-500">{produtos.length} itens cadastrados · medicamentos, dietas, higiene e materiais</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" className="gap-2" onClick={abrirMassa} data-testid="precos-massa-btn"><ListChecks className="w-4 h-4" /> Preços em massa</Button>
          )}
          <Button variant="outline" className="gap-2" onClick={() => setCatOpen(true)} data-testid="gerenciar-categorias-btn"><Tag className="w-4 h-4" /> Categorias</Button>
          <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={abrirNovo} data-testid="novo-produto-btn"><Plus className="w-4 h-4" /> Novo Produto</Button>
        </div>
      </div>

      {/* Resumo financeiro do estoque (margem) */}
      {isAdmin && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="resumo-margem">
          <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center"><Wallet className="w-5 h-5" /></div>
            <div>
              <p className="text-sm text-slate-400">Investido em estoque</p>
              <p className="text-xl font-bold text-slate-800" data-testid="total-investido">{brl(resumo.investido)}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><DollarSign className="w-5 h-5" /></div>
            <div>
              <p className="text-sm text-slate-400">Valor potencial de venda</p>
              <p className="text-xl font-bold text-slate-800" data-testid="valor-potencial">{brl(resumo.potencial)}</p>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-5 shadow-sm flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><TrendingUp className="w-5 h-5" /></div>
            <div>
              <p className="text-sm text-slate-400">Margem potencial</p>
              <p className="text-xl font-bold text-emerald-700" data-testid="margem-potencial">{brl(resumo.margem)}</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input className={`${inputCls} pl-9`} placeholder="Buscar produto..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="produto-busca" />
        </div>
        <div className="w-56">
          <Select value={filtroCat} onValueChange={setFiltroCat}>
            <SelectTrigger data-testid="filtro-categoria"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas as categorias</SelectItem>
              {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="px-5 py-3 font-medium">Produto</th>
                <th className="px-5 py-3 font-medium">Categoria</th>
                <th className="px-5 py-3 font-medium">Unid.</th>
                <th className="px-5 py-3 font-medium text-right">Preço compra</th>
                <th className="px-5 py-3 font-medium text-right">Preço venda</th>
                <th className="px-5 py-3 font-medium text-right">Margem</th>
                <th className="px-5 py-3 font-medium text-right">Estoque</th>
                <th className="px-5 py-3 font-medium text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {lista.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-8">Nenhum produto encontrado.</td></tr>}
              {lista.map((p) => {
                const margem = (Number(p.precoVenda) || 0) - (Number(p.precoCompra) || 0);
                return (
                <tr key={p.id} className="hover:bg-slate-50/60" data-testid={`produto-row-${p.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-700">{p.nome}</td>
                  <td className="px-5 py-3"><span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-600">{p.categoria}</span></td>
                  <td className="px-5 py-3 text-slate-500">{p.unidade}</td>
                  <td className="px-5 py-3 text-right text-slate-500">{brl(p.precoCompra)}</td>
                  <td className="px-5 py-3 text-right font-semibold text-slate-700">{brl(p.precoVenda)}</td>
                  <td className={`px-5 py-3 text-right font-medium ${margem > 0 ? "text-emerald-600" : margem < 0 ? "text-red-500" : "text-slate-400"}`}>{brl(margem)}</td>
                  <td className="px-5 py-3 text-right text-slate-600">{p.estoqueEmpresa}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={() => { setEntradaId(p.id); setQtd(""); }} className="text-slate-400 hover:text-emerald-600" title="Entrada de estoque" data-testid={`entrada-${p.id}`}><PackagePlus className="w-4 h-4" /></button>
                      <button onClick={() => abrirEdit(p)} className="text-slate-400 hover:text-blue-600" title="Editar" data-testid={`editar-produto-${p.id}`}><Pencil className="w-4 h-4" /></button>
                      {isAdmin && (
                        <button onClick={() => setExcluir(p)} className="text-slate-400 hover:text-red-600" title="Excluir produto" data-testid={`excluir-produto-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog produto */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} data-testid="produto-modal">
          <DialogHeader><DialogTitle>{editing ? "Editar Produto" : "Novo Produto"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-sm text-slate-600 mb-1 block">Nome</label><input className={inputCls} value={form.nome} onChange={(e) => s("nome", e.target.value)} data-testid="produto-nome" /></div>
            <div><label className="text-sm text-slate-600 mb-1 block">Categoria</label>
              <Select value={form.categoria} onValueChange={(v) => s("categoria", v)}>
                <SelectTrigger data-testid="produto-categoria"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm text-slate-600 mb-1 block">Unidade</label><input className={inputCls} value={form.unidade} onChange={(e) => s("unidade", e.target.value)} /></div>
            <div><label className="text-sm text-slate-600 mb-1 block">Preço de compra (R$)</label><input type="number" step="0.01" className={inputCls} value={form.precoCompra} onChange={(e) => s("precoCompra", parseFloat(e.target.value) || 0)} data-testid="produto-preco-compra" /></div>
            <div><label className="text-sm text-slate-600 mb-1 block">Preço de venda (R$)</label><input type="number" step="0.01" className={inputCls} value={form.precoVenda} onChange={(e) => s("precoVenda", parseFloat(e.target.value) || 0)} data-testid="produto-preco-venda" /></div>
            <div><label className="text-sm text-slate-600 mb-1 block">Estoque mínimo</label><input type="number" className={inputCls} value={form.estoqueMinimo} onChange={(e) => s("estoqueMinimo", parseInt(e.target.value) || 0)} /></div>
            {!editing && <div><label className="text-sm text-slate-600 mb-1 block">Estoque inicial</label><input type="number" className={inputCls} value={form.estoqueInicial} onChange={(e) => s("estoqueInicial", parseInt(e.target.value) || 0)} /></div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={salvar} data-testid="produto-salvar">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog entrada estoque */}
      <Dialog open={!!entradaId} onOpenChange={(o) => !o && setEntradaId(null)}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Entrada de Estoque</DialogTitle></DialogHeader>
          <label className="text-sm text-slate-600">Quantidade a adicionar</label>
          <input type="number" className={inputCls} value={qtd} onChange={(e) => setQtd(e.target.value)} data-testid="entrada-qtd" autoFocus />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEntradaId(null)}>Cancelar</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={registrarEntrada} data-testid="entrada-salvar">Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog confirmar exclusão de produto (somente admin) */}
      <Dialog open={!!excluir} onOpenChange={(o) => !o && setExcluir(null)}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-sm" data-testid="excluir-produto-modal">
          <DialogHeader><DialogTitle>Excluir produto</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            Tem certeza que deseja excluir <b>{excluir?.nome}</b>? Esta ação não pode ser desfeita.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExcluir(null)}>Cancelar</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={excluindo} onClick={confirmarExcluir} data-testid="confirmar-excluir-produto-btn">
              {excluindo ? "Excluindo..." : "Excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog categorias */}
      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent aria-describedby={undefined} data-testid="categorias-modal">
          <DialogHeader><DialogTitle>Categorias de Produtos</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <input className={inputCls} placeholder="Nova categoria..." value={novaCat} onChange={(e) => setNovaCat(e.target.value)} data-testid="nova-categoria-input" />
            <Button className="bg-blue-600 hover:bg-blue-700 shrink-0" onClick={addCategoria} data-testid="add-categoria-btn"><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-1.5 max-h-72 overflow-y-auto mt-2">
            {(categorias || []).map((c) => (
              <div key={c.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50" data-testid={`categoria-row-${c.id}`}>
                {editCat?.id === c.id ? (
                  <input className={`${inputCls} py-1`} value={editCat.nome} onChange={(e) => setEditCat({ ...editCat, nome: e.target.value })} autoFocus />
                ) : (
                  <span className="text-sm text-slate-700">{c.nome}</span>
                )}
                <div className="flex items-center gap-2 ml-2">
                  {editCat?.id === c.id ? (
                    <>
                      <button onClick={salvarEdicaoCat} className="text-emerald-600 text-xs font-semibold" data-testid={`salvar-cat-${c.id}`}>Salvar</button>
                      <button onClick={() => setEditCat(null)} className="text-slate-400"><X className="w-4 h-4" /></button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setEditCat({ id: c.id, nome: c.nome })} className="text-slate-400 hover:text-blue-600"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => excluirCategoria(c.id)} className="text-slate-400 hover:text-red-600" data-testid={`excluir-cat-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {/* Dialog preços em massa */}
      <Dialog open={massaOpen} onOpenChange={setMassaOpen}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-3xl" data-testid="precos-massa-modal">
          <DialogHeader><DialogTitle>Atualizar preços em massa</DialogTitle></DialogHeader>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input className={`${inputCls} pl-9`} placeholder="Buscar produto..." value={massaBusca} onChange={(e) => setMassaBusca(e.target.value)} data-testid="massa-busca" />
            </div>
            <div className="w-52">
              <Select value={massaCat} onValueChange={setMassaCat}>
                <SelectTrigger data-testid="massa-filtro-categoria"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas as categorias</SelectItem>
                  {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="max-h-[50vh] overflow-y-auto mt-2 border border-slate-100 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left sticky top-0">
                <tr>
                  <th className="px-3 py-2 font-medium">Produto</th>
                  <th className="px-3 py-2 font-medium w-32 text-right">Compra (R$)</th>
                  <th className="px-3 py-2 font-medium w-32 text-right">Venda (R$)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {listaMassa.map((p) => (
                  <tr key={p.id} data-testid={`massa-row-${p.id}`}>
                    <td className="px-3 py-1.5 text-slate-700">{p.nome}<span className="block text-[11px] text-slate-400">{p.categoria}</span></td>
                    <td className="px-3 py-1.5">
                      <input type="number" step="0.01" className="w-full px-2 py-1.5 rounded border border-slate-200 text-right outline-none focus:border-blue-500"
                        value={precos[p.id]?.precoCompra ?? 0} onChange={(e) => setPreco(p.id, "precoCompra", e.target.value)} data-testid={`massa-compra-${p.id}`} />
                    </td>
                    <td className="px-3 py-1.5">
                      <input type="number" step="0.01" className="w-full px-2 py-1.5 rounded border border-slate-200 text-right outline-none focus:border-blue-500"
                        value={precos[p.id]?.precoVenda ?? 0} onChange={(e) => setPreco(p.id, "precoVenda", e.target.value)} data-testid={`massa-venda-${p.id}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMassaOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={salvandoMassa} onClick={salvarMassa} data-testid="massa-salvar">{salvandoMassa ? "Salvando..." : "Salvar preços"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Produtos;
